import type {PublicProfileStationeryTemplate} from '@settings/models/publicProfile.settings.interface';
import {VAT_ISSUE_CATALOG} from '@vat/engine/issues';
import {round2} from '@vat/engine/rounding';
import {profileAt} from '@vat/engine/tax-profile';
import type {VatAdjustmentAction, VatIssueType, VatKennzahlen, VatKz, VatLine, VatReturnChecklistState, VatRule, VatSourceType} from '@vat/engine/types';
import {VAT_CHECKLIST_COPY, VAT_EXPORT_COPY, VAT_FORM_LABELS, VAT_KZ_LABELS, VAT_PERIOD_TYPE_LABELS, VAT_RETURN_STATUS_LABELS, VAT_TAX_STATUS_LABELS} from '@vat/vat-copy';
import {VAT_EXPORT_KZ_ORDER, VatExportPropertyLabel, VatExportSnapshot, VatReturnAdjustment, VatReturnChecklistEntry} from './vat-export.types';
import {formatDateDE, formatDateTimeDE, formatEuro, formatFullEuro, formatPercent, kz83Label, periodLabel, steuernummerLabel} from './vat-format';

/**
 * Vordruck-PDF (Spec 7): Seite 1 an den amtlichen Vordruck erinnernd, Anlage A Herleitung je Kennzahl,
 * Anlage B Vorsteuer-Schlüssel je Immobilie, Anlage C Prüfliste und Korrekturen. Reine pdfmake-Definition
 * aus dem Freigabe-Snapshot; Briefpapier nur für die Anlagen (A6). Explizite Spaltenbreiten überall
 * (pdfmake clippt still), Summe fester Breiten ≤ 515 pt bei 40-pt-Rändern.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
export interface VatVordruckOptions {
    stationery: PublicProfileStationeryTemplate | null;
}

const PAGE_MARGIN_X = 40;
const COLOR_MUTED = '#6b7280';
const COLOR_HEADER_FILL = '#f3f4f6';
const COLOR_KZ_FILL = '#4f46e5';
const FONT_SMALL = 8;
const FONT_BASE = 9;

/** Amtliche Kz-Bezeichnungen – eine Quelle mit der Kennzahlen-Karte der App (`VAT_KZ_LABELS`). */
export const VAT_VORDRUCK_KZ_LABELS: Record<'81' | '86' | '48' | '66' | '39' | '83', string> = VAT_KZ_LABELS;

export const VAT_RULE_LABELS: Record<VatRule, string> = {
    SOLL: 'Soll', IST: 'Ist', MINDEST_IST: 'Mindest-Ist', MINDEST_IST_ABZUG: 'Mindest-Ist-Abzug', MINDERUNG_17: 'Minderung § 17',
    VERRECHNUNG: 'Verrechnung', VORSTEUER: 'Vorsteuer', KU_ALTUMSATZ: 'KU-Altumsatz', ADJUSTMENT: 'Korrektur', AUSGESCHLOSSEN: 'ausgeschlossen',
};

export const VAT_SOURCE_TYPE_LABELS: Record<VatSourceType, string> = {BOOKING: 'Sollstellung', PAYMENT: 'Zahlung', EXPENSE: 'Ausgabe'};

export const VAT_ADJUSTMENT_ACTION_LABELS: Record<VatAdjustmentAction, string> = {
    EXCLUDE: 'Ausschluss', SET_CATEGORY: 'Kategorie gesetzt', SET_INPUT_TAX_SHARE: 'Vorsteuer-Anteil gesetzt',
    DURCHLAUFEND: 'Durchlaufender Posten', FORDERUNGSAUSFALL: 'Forderungsausfall (§ 17)', NACHTRAG_17: 'Nachtrag § 17',
};

/** ERLEDIGT/UEBERSPRUNGEN aus der Prüflisten-Copy der App; OFFEN kommt nur im Snapshot-Export vor. */
export const VAT_CHECKLIST_STATE_LABELS: Record<VatReturnChecklistState, string> = {OFFEN: 'offen', ...VAT_CHECKLIST_COPY.state};

export interface VordruckKzRow {
    kz: string;
    label: string;
    bmg: number | null;
    steuer: number | null;
}

export const buildKennzahlenRows = (kz: VatKennzahlen): VordruckKzRow[] => {
    const rows: VordruckKzRow[] = [
        {kz: '81', label: VAT_VORDRUCK_KZ_LABELS['81'], bmg: kz.kz81, steuer: kz.steuer81Elster},
        {kz: '86', label: VAT_VORDRUCK_KZ_LABELS['86'], bmg: kz.kz86, steuer: kz.steuer86Elster},
        {kz: '48', label: VAT_VORDRUCK_KZ_LABELS['48'], bmg: kz.kz48, steuer: null},
        {kz: '66', label: VAT_VORDRUCK_KZ_LABELS['66'], bmg: null, steuer: kz.kz66},
    ];
    if (kz.kz39 !== 0) {
        rows.push({kz: '39', label: VAT_VORDRUCK_KZ_LABELS['39'], bmg: null, steuer: kz.kz39});
    }
    rows.push({kz: '83', label: kz83Label(kz.kz83), bmg: null, steuer: kz.kz83});
    return rows;
};

const compareLines = (a: VatLine, b: VatLine): number => a.monthKey.localeCompare(b.monthKey) || a.id.localeCompare(b.id);

export const groupLinesByKz = (lines: VatLine[]): Record<VatKz, VatLine[]> => {
    const grouped: Record<VatKz, VatLine[]> = {'81': [], '86': [], '48': [], '66': [], NONE: []};
    [...lines].sort(compareLines).forEach(line => grouped[line.kz]?.push(line));
    return grouped;
};

export const propertyLabel = (properties: VatExportPropertyLabel[], propertyID: string): string =>
    properties.find(p => p.id === propertyID)?.label ?? propertyID;

export interface InputTaxByPropertyRow {
    propertyID: string;
    label: string;
    lineCount: number;
    vorsteuerGesamt: number;
    anrechenbar: number;
    effektiverAnteil: number;
}

/** Anlage B aus den VORSTEUER-Zeilen (A3): Modus/Flächen sind nicht Teil des Snapshots. */
export const summarizeInputTaxByProperty = (lines: VatLine[], properties: VatExportPropertyLabel[]): InputTaxByPropertyRow[] => {
    const byProperty = new Map<string, InputTaxByPropertyRow>();
    lines.filter(line => line.rule === 'VORSTEUER').forEach(line => {
        const row = byProperty.get(line.source.propertyID) ?? {
            propertyID: line.source.propertyID, label: propertyLabel(properties, line.source.propertyID), lineCount: 0, vorsteuerGesamt: 0, anrechenbar: 0, effektiverAnteil: 0,
        };
        row.lineCount += 1;
        row.vorsteuerGesamt = round2(row.vorsteuerGesamt + line.tax);
        row.anrechenbar = round2(row.anrechenbar + round2(line.tax * line.share));
        byProperty.set(line.source.propertyID, row);
    });
    return [...byProperty.values()]
        .map(row => ({...row, effektiverAnteil: row.vorsteuerGesamt === 0 ? 0 : Math.round((row.anrechenbar / row.vorsteuerGesamt) * 10000) / 10000}))
        .sort((a, b) => a.label.localeCompare(b.label, 'de'));
};

/** Prüflisten-Schlüssel `${type}_${key}` → Katalog-Titel; längster passender Typ-Präfix gewinnt. */
export const resolveIssueTitle = (checklistKey: string): string => {
    const types = (Object.keys(VAT_ISSUE_CATALOG) as VatIssueType[]).filter(type => checklistKey === type || checklistKey.startsWith(`${type}_`));
    if (!types.length) {
        return checklistKey;
    }
    const best = types.sort((a, b) => b.length - a.length)[0];
    return VAT_ISSUE_CATALOG[best].title;
};

export const buildVordruckFooterText = (snapshot: VatExportSnapshot): string => VAT_EXPORT_COPY.pdf.footer({
    datum: formatDateDE(snapshot.now),
    version: snapshot.vatReturn.version,
    engineVersion: snapshot.vatReturn.engineVersion,
    inputHash: snapshot.vatReturn.inputHash ?? '–',
});

export const buildStationeryImages = (stationery: PublicProfileStationeryTemplate | null): Record<string, string> => {
    const images: Record<string, string> = {};
    if (stationery?.url) {
        images['stationeryBackground'] = stationery.url;
    }
    if (stationery?.urlSecondPage) {
        images['stationeryBackgroundSecond'] = stationery.urlSecondPage;
    }
    return images;
};

/** Seite 1 nie; Seite 2 Hauptbogen; ab Seite 3 Folgebogen, falls hochgeladen (Muster getPdfBackground). */
export const buildStationeryBackground = (stationery: PublicProfileStationeryTemplate | null): ((currentPage: number, pageSize: { width: number; height: number }) => any) | null => {
    if (!stationery?.url) {
        return null;
    }
    return (currentPage: number, pageSize: { width: number; height: number }): any => {
        if (currentPage === 1) {
            return null;
        }
        const image = currentPage === 2 ? 'stationeryBackground' : (stationery.urlSecondPage ? 'stationeryBackgroundSecond' : null);
        return image ? {image, width: pageSize.width, height: pageSize.height, absolutePosition: {x: 0, y: 0}} : null;
    };
};

// ---------------------------------------------------------------------------
// Bausteine
// ---------------------------------------------------------------------------

const headerCell = (text: string, alignment: 'left' | 'right' | 'center' = 'left'): any =>
    ({text, bold: true, fontSize: FONT_SMALL, fillColor: COLOR_HEADER_FILL, alignment, margin: [2, 3, 2, 3]});

const cell = (text: string, options: Record<string, unknown> = {}): any => ({text, fontSize: FONT_SMALL, margin: [2, 2, 2, 2], ...options});

const sectionTitle = (text: string, id?: string, pageBreak?: 'before'): any =>
    ({text, id, pageBreak, bold: true, fontSize: 12, margin: [0, 8, 0, 6]});

const infoRow = (label: string, value: string): any[] => [cell(label, {color: COLOR_MUTED}), cell(value)];

const buildPage1 = (snapshot: VatExportSnapshot, kz: VatKennzahlen): any[] => {
    const {vatReturn, landlord} = snapshot;
    const profile = vatReturn.taxProfileSnapshot;
    const label = periodLabel(vatReturn.year, vatReturn.periodKey);
    const isCorrection = vatReturn.version > 1;
    // Für den Zeitraum gültiger Eintrag der Besteuerungs-Historie (größtes abJahr ≤ Jahr), nicht `besteuerung[0]` (= ältester).
    const form = profile ? profileAt(profile, vatReturn.startDate).form : 'SOLL';

    const infoBody: any[][] = [
        infoRow('Steuernummer', profile ? steuernummerLabel(profile) : '–'),
        infoRow('Finanzamt', profile?.finanzamt ? `${profile.finanzamt.name} (BUFA ${profile.finanzamt.bufaNr})` : '–'),
        infoRow('Unternehmer / Eigentümer', [landlord.name, landlord.addressLine].filter(Boolean).join(', ')),
        infoRow('Voranmeldungszeitraum', `${label} (${formatDateDE(vatReturn.startDate)} – ${formatDateDE(vatReturn.endDate)})`),
        infoRow('Besteuerung', profile ? `${VAT_TAX_STATUS_LABELS[profile.status]} · ${VAT_FORM_LABELS[form]}` : '–'),
        infoRow('Abgabe', profile ? `${VAT_PERIOD_TYPE_LABELS[profile.voranmeldungszeitraum]} · Dauerfristverlängerung: ${profile.dauerfristverlaengerung ? 'ja' : 'nein'}` : '–'),
        infoRow('USt-IdNr.', profile?.ustIdNr ?? '–'),
        infoRow('Status', `${VAT_RETURN_STATUS_LABELS[vatReturn.status]}${vatReturn.uebermittelt ? ` · übermittelt am ${formatDateDE(vatReturn.uebermittelt.am)}` : ''}`),
    ];

    const kzBody: any[][] = [
        VAT_EXPORT_COPY.pdf.kzTableHeaders.map((h, i) => headerCell(h, i >= 2 ? 'right' : 'left')),
        ...buildKennzahlenRows(kz).map(row => [
            cell(row.kz, {bold: true, color: '#ffffff', fillColor: COLOR_KZ_FILL, alignment: 'center'}),
            cell(row.label),
            cell(row.bmg === null ? '' : formatFullEuro(row.bmg), {alignment: 'right'}),
            cell(row.steuer === null ? '' : formatEuro(row.steuer), {alignment: 'right', bold: row.kz === '83'}),
        ]),
    ];

    return [
        {
            id: 'page1-header',
            columns: [
                {
                    stack: [
                        {text: `${VAT_EXPORT_COPY.pdf.title} ${label}`, bold: true, fontSize: 16},
                        {text: VAT_EXPORT_COPY.pdf.subtitle, color: COLOR_MUTED, fontSize: FONT_BASE},
                    ], width: '*',
                },
                {text: 'Beta', bold: true, color: '#ffffff', fillColor: COLOR_KZ_FILL, alignment: 'center', width: 60, margin: [0, 4, 0, 4]},
            ],
            margin: [0, 0, 0, 12],
        },
        {id: 'page1-info', table: {widths: [150, '*'], body: infoBody}, layout: 'lightHorizontalLines', margin: [0, 0, 0, 12]},
        {
            columns: [
                {id: 'kz10-box', table: {widths: [12], body: [[{text: isCorrection ? 'X' : ' ', bold: true, alignment: 'center', fontSize: FONT_BASE, margin: [0, 0, 0, 0]}]]}, width: 22},
                {text: `${VAT_EXPORT_COPY.pdf.kz10}${isCorrection && vatReturn.korrekturVonID ? ` – ${VAT_EXPORT_COPY.pdf.replaces(vatReturn.korrekturVonID)}` : ''}`, fontSize: FONT_BASE, margin: [4, 2, 0, 0], width: '*'},
            ],
            margin: [0, 0, 0, 10],
        },
        {id: 'kz-table', table: {headerRows: 1, widths: [34, '*', 120, 100], body: kzBody}, layout: 'lightHorizontalLines'},
        {id: 'cent-info', text: VAT_EXPORT_COPY.pdf.centInfo(formatEuro(kz.steuer81Cent), formatEuro(kz.steuer86Cent)), fontSize: FONT_SMALL, color: COLOR_MUTED, margin: [0, 6, 0, 0]},
        {
            id: 'release-line',
            text: vatReturn.freigabe ? VAT_EXPORT_COPY.pdf.releasedBy(vatReturn.freigabe.displayName, formatDateTimeDE(vatReturn.freigabe.at)) : VAT_EXPORT_COPY.pdf.notReleased,
            fontSize: FONT_SMALL, color: COLOR_MUTED, margin: [0, 2, 0, 0],
        },
    ];
};

/**
 * Anlage-A-Tabelle: Monat · Regel · Quelle · Immobilie · Bezeichnung · Brutto · Netto · Steuer · Anteil.
 * A4 hoch bietet 595,28 − 2·40 = 515 pt. Feste Breiten 390 pt + 9 Spalten · 4 pt Zellabstand = 426 pt,
 * damit bleiben der '*'-Spalte „Bezeichnung“ ≥ 80 pt – pdfmake schneidet zu breite Tabellen sonst still rechts ab.
 */
const ANLAGE_A_WIDTHS: Array<number | '*'> = [36, 58, 54, 60, '*', 48, 48, 50, 36];
const ANLAGE_A_FONT = 7;

/** Wie 'lightHorizontalLines', aber mit 2 pt statt 4 pt Zellabstand – Platz für neun Spalten auf A4 hoch. */
const ANLAGE_A_LAYOUT = {
    hLineWidth: (i: number, node: any): number => (i === 0 || i === 1 || i === node.table.body.length ? 1 : 0.5),
    vLineWidth: (): number => 0,
    hLineColor: (i: number, node: any): string => (i === 0 || i === 1 || i === node.table.body.length ? '#9ca3af' : '#e5e7eb'),
    paddingLeft: (): number => 2,
    paddingRight: (): number => 2,
    paddingTop: (): number => 2,
    paddingBottom: (): number => 2,
};

const lineCell = (text: string, options: Record<string, unknown> = {}): any => cell(text, {fontSize: ANLAGE_A_FONT, margin: [0, 1, 0, 1], ...options});

const buildLinesTable = (id: string, lines: VatLine[], properties: VatExportPropertyLabel[]): any => {
    const header = ['Monat', 'Regel', 'Quelle', 'Immobilie', 'Bezeichnung', 'Brutto', 'Netto', 'Steuer', 'Anteil']
        .map((h, i) => ({...headerCell(h, i >= 5 ? 'right' : 'left'), fontSize: ANLAGE_A_FONT, margin: [0, 2, 0, 2]}));
    const body = lines.map(line => [
        lineCell(line.monthKey),
        lineCell(VAT_RULE_LABELS[line.rule] ?? line.rule),
        lineCell(VAT_SOURCE_TYPE_LABELS[line.source.type] ?? line.source.type),
        lineCell(propertyLabel(properties, line.source.propertyID)),
        lineCell(line.label),
        lineCell(formatEuro(line.gross), {alignment: 'right'}),
        lineCell(formatEuro(line.net), {alignment: 'right'}),
        lineCell(formatEuro(line.tax), {alignment: 'right'}),
        lineCell(formatPercent(line.share), {alignment: 'right'}),
    ]);
    const sum = (pick: (l: VatLine) => number): number => round2(lines.reduce((acc, l) => acc + pick(l), 0));
    const total = [lineCell('Summe', {bold: true}), lineCell(''), lineCell(''), lineCell(''), lineCell(`${lines.length} Zeile${lines.length === 1 ? '' : 'n'}`, {color: COLOR_MUTED}),
        lineCell(formatEuro(sum(l => l.gross)), {alignment: 'right', bold: true}), lineCell(formatEuro(sum(l => l.net)), {alignment: 'right', bold: true}),
        lineCell(formatEuro(sum(l => l.tax)), {alignment: 'right', bold: true}), lineCell('')];
    return {id, table: {headerRows: 1, widths: ANLAGE_A_WIDTHS, body: [header, ...body, total]}, layout: ANLAGE_A_LAYOUT, margin: [0, 0, 0, 10]};
};

const buildAnlageA = (snapshot: VatExportSnapshot): any[] => {
    const grouped = groupLinesByKz(snapshot.lines);
    const content: any[] = [sectionTitle(VAT_EXPORT_COPY.pdf.anlageA, 'anlage-a', 'before')];
    VAT_EXPORT_KZ_ORDER.forEach(kz => {
        const lines = grouped[kz];
        if (!lines.length) {
            return;
        }
        content.push({text: `Kennzahl ${kz} – ${VAT_VORDRUCK_KZ_LABELS[kz as '81' | '86' | '48' | '66']}`, bold: true, fontSize: FONT_BASE, margin: [0, 4, 0, 4]});
        content.push(buildLinesTable(`anlage-a-${kz}`, lines, snapshot.properties));
    });
    if (grouped.NONE.length) {
        content.push({text: VAT_EXPORT_COPY.pdf.anlageAExcluded, bold: true, fontSize: FONT_BASE, color: COLOR_MUTED, margin: [0, 4, 0, 4]});
        content.push(buildLinesTable('anlage-a-none', grouped.NONE, snapshot.properties));
    }
    return content;
};

const buildAnlageB = (snapshot: VatExportSnapshot): any[] => {
    const rows = summarizeInputTaxByProperty(snapshot.lines, snapshot.properties);
    const header = ['Immobilie', 'Vorsteuer-Zeilen', 'Vorsteuer gesamt', 'anrechenbar (Kz 66)', 'effektiver Anteil'].map((h, i) => headerCell(h, i >= 1 ? 'right' : 'left'));
    const body = rows.map(row => [
        cell(row.label), cell(String(row.lineCount), {alignment: 'right'}), cell(formatEuro(row.vorsteuerGesamt), {alignment: 'right'}),
        cell(formatEuro(row.anrechenbar), {alignment: 'right'}), cell(formatPercent(row.effektiverAnteil), {alignment: 'right'}),
    ]);
    return [
        sectionTitle(VAT_EXPORT_COPY.pdf.anlageB),
        {text: VAT_EXPORT_COPY.pdf.anlageBHint, fontSize: FONT_SMALL, color: COLOR_MUTED, margin: [0, 0, 0, 6]},
        rows.length
            ? {id: 'anlage-b', table: {headerRows: 1, widths: ['*', 70, 90, 100, 80], body: [header, ...body]}, layout: 'lightHorizontalLines'}
            : {id: 'anlage-b', text: VAT_EXPORT_COPY.pdf.none, fontSize: FONT_SMALL, color: COLOR_MUTED},
    ];
};

const buildAnlageC = (snapshot: VatExportSnapshot): any[] => {
    const checklistEntries = Object.entries(snapshot.vatReturn.checklist ?? {}).sort(([a], [b]) => a.localeCompare(b)) as Array<[string, VatReturnChecklistEntry]>;
    const checklistHeader = ['Prüfpunkt', 'Status', 'Begründung / Notiz', 'von', 'am'].map(h => headerCell(h));
    const checklistBody = checklistEntries.map(([key, entry]) => [
        cell(resolveIssueTitle(key)), cell(VAT_CHECKLIST_STATE_LABELS[entry.state] ?? entry.state), cell(entry.note ?? ''), cell(entry.by ?? ''), cell(formatDateDE(entry.at)),
    ]);

    const adjustments = [...(snapshot.vatReturn.adjustments ?? [])].sort((a: VatReturnAdjustment, b: VatReturnAdjustment) => a.createdAt.getTime() - b.createdAt.getTime());
    const adjustmentHeader = ['Aktion', 'Ziel', 'Wert', 'Begründung', 'von', 'am'].map(h => headerCell(h));
    const adjustmentBody = adjustments.map(adj => [
        cell(VAT_ADJUSTMENT_ACTION_LABELS[adj.action] ?? adj.action), cell(`${adj.targetType} ${adj.targetID}`),
        cell(adj.value === null || adj.value === undefined ? '' : typeof adj.value === 'number' ? formatEuro(adj.value) : String(adj.value)),
        cell(adj.reason), cell(adj.displayName), cell(formatDateDE(adj.createdAt)),
    ]);

    return [
        sectionTitle(VAT_EXPORT_COPY.pdf.anlageC),
        {text: VAT_EXPORT_COPY.pdf.checklist, bold: true, fontSize: FONT_BASE, margin: [0, 4, 0, 4]},
        checklistBody.length
            ? {id: 'anlage-c-checklist', table: {headerRows: 1, widths: ['*', 60, 150, 80, 60], body: [checklistHeader, ...checklistBody]}, layout: 'lightHorizontalLines', margin: [0, 0, 0, 10]}
            : {id: 'anlage-c-checklist', text: VAT_EXPORT_COPY.pdf.none, fontSize: FONT_SMALL, color: COLOR_MUTED, margin: [0, 0, 0, 10]},
        {text: VAT_EXPORT_COPY.pdf.adjustments, bold: true, fontSize: FONT_BASE, margin: [0, 4, 0, 4]},
        adjustmentBody.length
            ? {id: 'anlage-c-adjustments', table: {headerRows: 1, widths: [80, 90, 60, '*', 70, 55], body: [adjustmentHeader, ...adjustmentBody]}, layout: 'lightHorizontalLines'}
            : {id: 'anlage-c-adjustments', text: VAT_EXPORT_COPY.pdf.none, fontSize: FONT_SMALL, color: COLOR_MUTED},
    ];
};

export const buildVatVordruckDocDefinition = (snapshot: VatExportSnapshot, options: VatVordruckOptions = {stationery: null}): any => {
    // Spec 7: Exporte entstehen nur aus dem Freigabe-Snapshot, dort sind Kennzahlen gesetzt. Ein null-Snapshot ist eine
    // Invariantenverletzung – lieber scheitern als ein Dokument mit stillen Nullen an die Steuerberatung geben.
    if (!snapshot.vatReturn.kennzahlen) {
        throw new Error('Vordruck-PDF: Snapshot ohne Kennzahlen (nicht freigegeben)');
    }
    const kennzahlen = snapshot.vatReturn.kennzahlen;
    const footerText = buildVordruckFooterText(snapshot);
    const hasStationery = !!options.stationery?.url;
    const label = periodLabel(snapshot.vatReturn.year, snapshot.vatReturn.periodKey);
    return {
        info: {title: `Umsatzsteuer-Voranmeldung ${label} – ${snapshot.landlord.name} – v${snapshot.vatReturn.version}`, author: 'Nahaus'},
        pageSize: 'A4',
        pageMargins: [PAGE_MARGIN_X, hasStationery ? 90 : 50, PAGE_MARGIN_X, 60],
        background: buildStationeryBackground(options.stationery),
        images: buildStationeryImages(options.stationery),
        content: [...buildPage1(snapshot, kennzahlen), ...buildAnlageA(snapshot), ...buildAnlageB(snapshot), ...buildAnlageC(snapshot)],
        footer: (currentPage: number, pageCount: number): any => ({
            stack: [
                {text: footerText, fontSize: 7, color: COLOR_MUTED, alignment: 'center', margin: [PAGE_MARGIN_X, 0, PAGE_MARGIN_X, 0]},
                {text: VAT_EXPORT_COPY.pdf.page(currentPage, pageCount), fontSize: 7, color: COLOR_MUTED, alignment: 'center', margin: [0, 2, 0, 0]},
            ],
        }),
        defaultStyle: {fontSize: FONT_BASE},
    };
};
